import { PlusIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";

import { useSchemaEditor } from "../..";
import ArrayTypeSwitch from "../ArrayTypeSwitch";
import Enums from "../Enums";
import ParameterContent from "../ParameterContent";
import ParameterMetadata from "../ParametersMetadata";
import CompositionPropertyPanel from "../CompositionPropertyPanel";
import { ISchemaParameter } from "../schema-parameter-store";
import Variable from "../Variable";

import { JSONSchemaParameterContent } from "./JSONSchemaParameterContent";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  parameter: ISchemaParameter;
}

const SelectRef = observer(({ parameter }: Props) => {
  const editor = useSchemaEditor();
  const defs = editor.$defs;
  if (!defs || defs.length === 0) {
    return null;
  }
  const { isReadonly } = editor;

  return (
    <Select
      disabled={isReadonly}
      value={parameter.$ref || "none"}
      onValueChange={value => parameter.setRef(value === "none" ? "" : value)}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select $ref" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        {defs.map(def => (
          <SelectItem key={def.name} value={`#/$defs/${def.name}`}>
            {def.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});

function renderParameterBody(parameter: ISchemaParameter, isReadonly: boolean) {
  if (parameter.hasComposition) {
    return <CompositionPropertyPanel parameter={parameter} />;
  }
  if (parameter.type === "variable") {
    return (
      <Variable
        variableName={parameter.variableName}
        onNameChange={name => !isReadonly && parameter.setVariableName(name)}
        isReadonly={isReadonly}
        errors={parameter.isSubmitAttempted ? parameter.errors : {}}
      />
    );
  }
  return null;
}

const SchemaParameterContent = observer(({ parameter }: Props) => {
  const schema = useSchemaEditor();
  const { isReadonly } = schema;
  const buttonIndentStyle = {
    marginLeft: `${(parameter.depth + 1) * 6}px`,
    position: "relative" as const,
  };
  if (!parameter.isExpanded) {
    return null;
  }

  const parameterBody = renderParameterBody(parameter, isReadonly);

  return (
    <div className="ml-3 flex flex-col gap-y-1">
      <div className="flex items-center gap-2">
        <div className="grid w-full grid-cols-[1fr_auto] gap-2">
          <ParameterMetadata
            {...parameter.metadata}
            hideTypeControls={parameter.hasComposition}
            errors={parameter.isSubmitAttempted ? parameter.errors : {}}
            onChangeName={value => !isReadonly && parameter.setName(value)}
            onChangeType={type => !isReadonly && parameter.setType(type)}
            onChangeTitle={value => !isReadonly && parameter.setTitle(value)}
            onChangeRequired={() => !isReadonly && parameter.toggleRequired()}
            onChangeNullable={value => !isReadonly && parameter.setNullable(value)}
          />
          {!parameter.hasComposition && <SelectRef parameter={parameter} />}
        </div>
      </div>

      {parameterBody ?? (
        <ParameterContent
          parameter={parameter}
          before={
            parameter.type === "array" && (
              <ArrayTypeSwitch
                checked={parameter.isCompositionArray}
                onChange={() => {
                  if (!isReadonly) {
                    parameter.toggleArrayType();
                  }
                }}
                disabled={isReadonly}
              />
            )
          }
        >
          {parameter.enumsAvailable && (
            <Enums
              enums={parameter._enum}
              enumName={parameter.enumName}
              includeEnum={parameter.hasEnums}
              isEnumVariable={parameter.isEnumVariable}
              onNameChange={name => !isReadonly && parameter.setEnumName(name)}
              toggleEnumMode={() => !isReadonly && parameter.toggleEnumMode()}
              addEnum={() => !isReadonly && parameter.addEnum()}
              setEnum={(k, v) => !isReadonly && parameter.setEnum(k, v)}
              toggleEnums={() => !isReadonly && parameter.toggleEnums()}
              deleteEnum={k => !isReadonly && parameter.deleteEnum(k)}
            />
          )}
          <JSONSchemaParameterContent parameter={parameter} />
          {parameter.isEditableList && !isReadonly ? (
            <div className="mb-4 w-full  pl-4">
              <Button
                onClick={() => {
                  parameter.addField();
                }}
                variant="outline"
                style={buttonIndentStyle}
                size="sm"
              >
                <PlusIcon className="mr-1 h-3 w-3" /> Add Field
              </Button>
            </div>
          ) : null}
        </ParameterContent>
      )}
    </div>
  );
});

export { SchemaParameterContent };
